import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';
import { authAPI, userAPI } from '../services/api';
import { logEvent, setUserProperties } from '../utils/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check for OAuth error in URL
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    
    if (error) {
      console.error('OAuth error detected:', error, errorDescription);
      // Clear error from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Could show error to user here if needed
    }
    
    checkAuth();
  }, []);


  const checkAuth = async (shouldRedirect = true) => {
    try {
      const cognitoUser = await getCurrentUser();

      if (cognitoUser) {
        // Verify with backend and get user data
        let response;
        try {
          response = await authAPI.verify();
        } catch (verifyError) {
          console.error('checkAuth - Verify API call failed:', verifyError);
          console.error('checkAuth - Verify error details:', {
            message: verifyError.message,
            response: verifyError.response?.data,
            status: verifyError.response?.status,
            code: verifyError.code,
            config: {
              url: verifyError.config?.url,
              baseURL: verifyError.config?.baseURL,
              method: verifyError.config?.method
            }
          });
          
          // If it's a network error (backend not reachable), show helpful message
          if (verifyError.code === 'ERR_NETWORK' || verifyError.message === 'Network Error' || verifyError.code === 'ECONNABORTED') {
            console.error('checkAuth - Network error: Backend API is not reachable');
            console.error('Error details:', {
              code: verifyError.code,
              message: verifyError.message,
              apiURL: process.env.REACT_APP_API_URL
            });
            
            // Check if this is a mixed content issue (HTTPS page trying HTTP request)
            if (typeof window !== 'undefined' && window.location.protocol === 'https:' && 
                process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.startsWith('http://')) {
              console.error('⚠️  Mixed Content Issue: HTTPS page trying to load HTTP resource');
              console.error('   Browser is blocking HTTP requests from HTTPS page for security');
              console.error('   Solution: Set up HTTPS for the backend API');
            }
            
            // Set error state so user sees something
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            
            // Show error alert to user
            alert('Unable to connect to the backend API. Please check the browser console for details.');
            return;
          }
          
          throw verifyError;
        }
        
        if (response.data.userExists) {
          // User exists in DynamoDB, fetch their data from there
          // Check if this was a social login
          const socialAuthIntent = localStorage.getItem('socialAuthIntent');
          const isSocialLogin = socialAuthIntent === 'login';
          
          // Clear any social auth intent flags since login was successful
          localStorage.removeItem('socialAuthIntent');
          
          // Fetch full user data including level
          const userResponse = await authAPI.getMe();
          const userData = userResponse.data;
          
          // Set user properties in GA
          setUserProperties({
            user_name: userData.name || '',
            user_email: userData.email || ''
          });
          
          // Track successful login (email or social)
          if (isSocialLogin) {
            logEvent('login', {
              category: 'user',
              method: 'google'
            });
          }
          
          setUser(userData);
          // Check if user is admin
          const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [];
          setIsAdmin(adminEmails.includes(userData.email));
          
          // Only redirect if shouldRedirect is true (for OAuth redirects and initial mount)
          // Don't redirect when called from manual login flow
          if (shouldRedirect) {
            const currentPath = window.location.pathname;
            if (currentPath === '/' || currentPath === '/login' || currentPath === '/signup') {
              window.location.href = '/leagues';
            }
          }
        } else {
          // User authenticated in Cognito but not in backend DB
          // Check if this was a login attempt or signup attempt
          const socialAuthIntent = localStorage.getItem('socialAuthIntent');
          
          if (socialAuthIntent === 'login') {
            // User tried to login with Google but account doesn't exist
            // DO NOT clear the flag yet - keep it so we don't auto-signup on redirect
            alert('No account found with this Google account. Please sign up first.');
            await amplifySignOut();
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            // Redirect to signup page
            window.location.href = '/signup';
            return;
          }
          
          // This is a signup attempt, proceed with registration
          // Clear the flag now that we're processing the signup
          localStorage.removeItem('socialAuthIntent');
          
          // Get user info from Cognito JWT token
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.payload;
          
          const email = idToken?.email || cognitoUser.username;
          // Get name from Cognito token (provided by Google)
          // Cognito stores Google name in 'name' field
          const cognitoName = idToken?.name || idToken?.given_name || idToken?.family_name;
          const fullName = cognitoName || email.split('@')[0];
          
          try {
            // Try to register the user
            const registerResponse = await authAPI.register({
              name: fullName,
              email: email,
              authProvider: 'google',
            });
            
            // User registered successfully (status 200 or 201)
            
            // Fetch the user data from DynamoDB
            const userResponse = await authAPI.getMe();
            const registeredUser = userResponse.data;
            
            // Set user properties in GA
            setUserProperties({
              user_name: registeredUser.name || '',
              user_email: registeredUser.email || ''
            });
            
            // Track successful signup
            logEvent('signup', {
              category: 'user',
              method: 'google'
            });
            
            setUser(registeredUser);
            const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [];
            setIsAdmin(adminEmails.includes(registeredUser.email));
            
            // Redirect new user to leagues (questionnaire is now per-sport and optional)
            setLoading(false);
            window.location.href = '/leagues';
            return;
          } catch (error) {
            // Handle registration errors
            console.error('Registration error:', error);
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      console.error('Error details:', error.response || error);
      
      // If user is not authenticated at all, clear any stale socialAuthIntent flags
      // This prevents old flags from interfering with new login attempts
      if (error.name === 'UserUnAuthenticatedException') {
        localStorage.removeItem('socialAuthIntent');
      }
      
      setUser(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      const refreshedUser = response.data;
      setUser(refreshedUser);
      const adminEmails = process.env.REACT_APP_ADMIN_EMAILS?.split(',') || [];
      setIsAdmin(adminEmails.includes(refreshedUser.email));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIsAdmin(false);
      // Clear all auth-related localStorage items
      localStorage.removeItem('socialAuthIntent');
      localStorage.removeItem('pendingUserData');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const value = {
    user,
    loading,
    isAdmin,
    checkAuth,
    refreshUser,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

